import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBridge } from '../../bridge/useBridge';
import { externalBankDefaults, externalBankSchema, type ExternalBankValues } from './schema';
import { Step1Initiate } from './steps/Step1Initiate';
import { Step2Callback } from './steps/Step2Callback';

/**
 * external-bank — Pay by Bank (PISP).
 *
 * The flow has two independent entry points (`initiate` and `callback`)
 * around an out-of-embed bank SCA. Each is rendered as its own
 * standalone screen — no Next button, no progress indicator, no
 * inter-step navigation — so we use a plain `<Routes>` instead of
 * `StepWizard`. Each step component emits `step:change` itself with
 * `{ stepIndex: 0, totalSteps: 1 }`.
 *
 * See: PRD `flows/external-bank/PRD.md` § 3 / § 11 O-2, and
 * `.claude/skills/pages-forms-to-react/LESSONS.md`.
 */
export const ExternalBankFlow: React.FC = () => {
  const bridge = useBridge();
  const methods = useForm<ExternalBankValues>({
    resolver: zodResolver(externalBankSchema),
    mode: 'onSubmit',
    defaultValues: externalBankDefaults,
  });

  // Honor `init.entryStep` once on mount — the host may have asked us to
  // land on `callback` directly (after the user returns from the bank).
  // The `<Navigate>` below is a fallback for direct navigation to /flows/external-bank.
  const location = useLocation();
  useEffect(() => {
    if (!bridge.entryStep) return;
    if (location.pathname.endsWith(`/${bridge.entryStep}`)) return;
    // Host-driven `goto` is delivered through bridge.onGoto, which is
    // wired at the App level — we don't need to navigate here. Leaving
    // this branch as a guarded no-op for clarity.
  }, [bridge.entryStep, location.pathname]);

  return (
    <FormProvider {...methods}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={bridge.entryStep === 'callback' ? 'callback' : 'initiate'} replace />}
        />
        <Route path="initiate" element={<Step1Initiate />} />
        <Route path="callback" element={<Step2Callback />} />
        <Route path="*" element={<Navigate to="initiate" replace />} />
      </Routes>
    </FormProvider>
  );
};

export default ExternalBankFlow;
